/*
  VoltX "Check My Home" wizard (redesign).

  Steps come from the section's blocks: a "Step" block starts a step and the
  question blocks after it belong to that step (Shopify has no nested blocks,
  so grouping is by order). Answers live in wizard.state keyed by each
  question's field name.

  Persistence, identity and uploads go through the Cloudflare Worker, same
  routes as the original wizard:
    POST  {workerBaseUrl}/draft                → create the anonymous contact, returns contactId
    GET   {workerBaseUrl}/contact?contactId=... → load { progressStep, progressData }
    PATCH {workerBaseUrl}/contact                → save { contactId, progressStep, progressData }
    POST  {workerBaseUrl}/upload-photo           → upload one file, returns { url }
    POST  {workerBaseUrl}/otp/send               → email a 4-digit code
    POST  {workerBaseUrl}/otp/verify             → verify the emailed code
    POST  {workerBaseUrl}/otp/send-sms           → SMS a 6-digit code
    POST  {workerBaseUrl}/otp/verify-sms         → verify the SMS code

  Flow: steps → the step holding the Contact block sends a code to the email
  or mobile entered → verify → HubSpot Forms submission → thank-you screen.
*/

(function () {
  'use strict';

  /* A separate key from the original wizard: its saved progress has a
     different shape and step numbering. */
  var DRAFT_KEY = 'voltxCheckMyHomeDraftV2';
  var SKIPPED = 'unanswered';

  /* ---------- Helpers ---------- */

  function escapeHtml(value) {
    return String(value == null ? '' : value).replace(/[&<>"']/g, function (char) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[char];
    });
  }

  function readConfig(root) {
    var tag = root.querySelector('[data-voltx-cmh-config]');
    if (!tag) return {};
    try {
      return JSON.parse(tag.textContent);
    } catch (error) {
      console.error('[Check My Home] Failed to parse config JSON:', error);
      return {};
    }
  }

  function readDraft() {
    try {
      var draft = JSON.parse(localStorage.getItem(DRAFT_KEY) || 'null');
      return draft && draft.contactId ? draft : null;
    } catch (error) {
      return null;
    }
  }

  function writeDraft(contactId) {
    try {
      localStorage.setItem(DRAFT_KEY, JSON.stringify({ contactId: contactId }));
    } catch (error) {
      /* private mode / blocked storage — progress still saves server-side this visit */
    }
  }

  function isValidEmail(value) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
  }

  function phoneDigits(value) {
    return String(value || '').replace(/[^\d]/g, '');
  }

  function isValidPhone(value) {
    var digits = phoneDigits(value);
    return digits.length >= 8 && digits.length <= 12 && !/[a-z@]/i.test(value);
  }

  function toE164(localOrIntl) {
    var digits = phoneDigits(localOrIntl);
    if (digits.charAt(0) === '0') digits = '61' + digits.slice(1);
    return digits;
  }

  function slugify(label) {
    return String(label).toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '');
  }

  /* One option per line: "Label" or "Label | stored_value" */
  function parseOptions(raw) {
    return String(raw || '')
      .split(/\r?\n/)
      .map(function (line) { return line.trim(); })
      .filter(Boolean)
      .map(function (line) {
        var parts = line.split('|');
        var label = parts[0].trim();
        return { label: label, value: (parts[1] || '').trim() || slugify(label) };
      });
  }

  function stateFromPostcode(postcode) {
    if (!/^\d{4}$/.test(postcode || '')) return '';
    var n = parseInt(postcode, 10);
    if ((n >= 1000 && n <= 2599) || (n >= 2619 && n <= 2899) || (n >= 2921 && n <= 2999)) return 'NSW';
    if ((n >= 200 && n <= 299) || (n >= 2600 && n <= 2618) || (n >= 2900 && n <= 2920)) return 'ACT';
    if ((n >= 3000 && n <= 3999) || (n >= 8000 && n <= 8999)) return 'VIC';
    if ((n >= 4000 && n <= 4999) || (n >= 9000 && n <= 9999)) return 'QLD';
    if (n >= 5000 && n <= 5999) return 'SA';
    if (n >= 6000 && n <= 6999) return 'WA';
    if (n >= 7000 && n <= 7999) return 'TAS';
    if (n >= 800 && n <= 999) return 'NT';
    return '';
  }

  function isStateAllowed(state, allowedStatesConfig) {
    if (!state) return false;
    var allowed = (allowedStatesConfig || '')
      .split(',')
      .map(function (item) { return item.trim().toUpperCase(); })
      .filter(Boolean);
    if (!allowed.length) return true;
    return allowed.indexOf(state.toUpperCase()) !== -1;
  }

  async function checkPostcodeServiceability(apiUrl, postcode) {
    var response = await fetch(apiUrl + '?postcode_filter=' + encodeURIComponent(postcode));
    if (!response.ok) throw new Error('Postcode lookup failed with status ' + response.status);
    var data = await response.json();
    if (!data || !data.results || !data.results.length) return null;
    return data.results[0].serviceability;
  }

  var googleMapsLoadPromise = null;

  function loadGoogleMaps(apiKey) {
    if (googleMapsLoadPromise) return googleMapsLoadPromise;
    googleMapsLoadPromise = new Promise(function (resolve, reject) {
      if (window.google && window.google.maps && window.google.maps.places) {
        resolve();
        return;
      }
      var script = document.createElement('script');
      script.src = 'https://maps.googleapis.com/maps/api/js?key=' + encodeURIComponent(apiKey) + '&libraries=places';
      script.async = true;
      script.onload = function () { resolve(); };
      script.onerror = function () { reject(new Error('Failed to load Google Maps')); };
      document.head.appendChild(script);
    });
    return googleMapsLoadPromise;
  }

  /* ---------- Icons ---------- */

  var ICONS = {
    user: '<svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true"><circle cx="8" cy="5.5" r="2.75" stroke="currentColor" stroke-width="1.2"/><path d="M2.75 13.5c0-2.6 2.35-4.5 5.25-4.5s5.25 1.9 5.25 4.5" stroke="currentColor" stroke-width="1.2" stroke-linecap="round"/></svg>',
    home: '<svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true"><path d="M2.5 7 8 2.5 13.5 7v6.25a.75.75 0 0 1-.75.75H3.25a.75.75 0 0 1-.75-.75V7Z" stroke="currentColor" stroke-width="1.2" stroke-linejoin="round"/><path d="M6.25 14v-4h3.5v4" stroke="currentColor" stroke-width="1.2" stroke-linejoin="round"/></svg>',
    pin: '<svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true"><path d="M8 14s4.5-4.2 4.5-7.5a4.5 4.5 0 1 0-9 0C3.5 9.8 8 14 8 14Z" stroke="currentColor" stroke-width="1.2" stroke-linejoin="round"/><circle cx="8" cy="6.5" r="1.6" stroke="currentColor" stroke-width="1.2"/></svg>',
    phone: '<svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true"><path d="M5.6 2.5H3.5a1 1 0 0 0-1 1.05C2.9 9.2 6.8 13.1 12.45 13.5a1 1 0 0 0 1.05-1v-2.1l-2.8-1.1-1.35 1.35a7.2 7.2 0 0 1-3.5-3.5L7.2 5.8 6.1 3Z" stroke="currentColor" stroke-width="1.2" stroke-linejoin="round"/></svg>',
    mail: '<svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true"><rect x="2" y="3.5" width="12" height="9" rx="1.5" stroke="currentColor" stroke-width="1.2"/><path d="m2.5 4.5 5.5 4 5.5-4" stroke="currentColor" stroke-width="1.2" stroke-linejoin="round"/></svg>',
    arrow: '<svg width="18" height="18" viewBox="0 0 28 28" fill="none" aria-hidden="true"><path d="M16.9925 14.0786L5.59128 22.9424H8.12003L19.4688 14.0786L7.93628 5.05738H5.43378L16.9925 14.0786Z" fill="currentColor"/><path d="M20.09 14.9711L9.8438 22.9424H12.3726L21.8663 15.5311C22.8113 14.7961 22.8113 13.3699 21.8663 12.6349L12.1888 5.06614H9.67755L20.09 13.1861C20.6676 13.6411 20.6676 14.5161 20.09 14.9711Z" fill="currentColor"/></svg>',
    error: '<svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true"><circle cx="8" cy="8" r="7" stroke="currentColor" stroke-width="1.5"/><path d="M8 4.5V8.75" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/><circle cx="8" cy="11.25" r="0.9" fill="currentColor"/></svg>',
    upload: '<svg width="26" height="26" viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M6.5 18.5a4.5 4.5 0 0 1-.4-8.98A6 6 0 0 1 17.7 8.6a4.2 4.2 0 0 1-.2 9.9h-11Z" fill="currentColor"/><path d="M12 16.5v-5.5M9.5 13l2.5-2.5 2.5 2.5" stroke="#FFFFFF" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/></svg>',
    check: '<svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true"><circle cx="8" cy="8" r="7" stroke="currentColor" stroke-width="1.4"/><path d="m5.2 8.2 1.9 1.9 3.8-4" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round"/></svg>',
    image: '<svg width="28" height="28" viewBox="0 0 24 24" fill="none" aria-hidden="true"><rect x="3" y="4" width="18" height="16" rx="2" stroke="currentColor" stroke-width="1.4"/><circle cx="9" cy="9.5" r="1.8" stroke="currentColor" stroke-width="1.4"/><path d="m4 18 5-5 4 4 3-3 4 4" stroke="currentColor" stroke-width="1.4" stroke-linejoin="round"/></svg>'
  };

  function errorSlot(key) {
    return (
      '<p class="voltx-cmh__error caption" data-error-slot="' + key + '" id="VoltxCmhError-' + key.replace(/[^a-z0-9-]/gi, '-') + '" hidden>' +
      ICONS.error + '<span data-error-text></span></p>'
    );
  }

  function errorId(key) {
    return 'VoltxCmhError-' + key.replace(/[^a-z0-9-]/gi, '-');
  }

  /* ---------- Config → steps ---------- */

  function buildSteps(items) {
    var steps = [];
    (items || []).forEach(function (item) {
      if (!item) return;
      if (item.kind === 'step') {
        steps.push({
          title: item.title || '',
          image: item.image || null,
          showSkip: item.showSkip !== false,
          questions: []
        });
        return;
      }
      if (!steps.length) steps.push({ title: '', image: null, showSkip: true, questions: [] });
      if (item.type === 'choice') item.options = parseOptions(item.options);
      if (item.type === 'upload') {
        item.boxes = (item.boxes || []).filter(function (box) { return box && box.title && box.fieldName; });
      }
      steps[steps.length - 1].questions.push(item);
    });
    return steps.filter(function (step) { return step.questions.length; });
  }

  /* Every place a question writes into wizard.state */
  function questionFields(q) {
    if (q.type === 'upload') return q.boxes.map(function (box) { return box.fieldName; });
    if (q.type === 'contact') return [q.nameField, q.fieldName, q.consentField].filter(Boolean);
    if (q.type === 'media' || q.type === 'review') return [];
    return q.fieldName ? [q.fieldName] : [];
  }

  function questionSlots(q) {
    switch (q.type) {
      case 'choice':
      case 'text':
        return ['main'];
      case 'address':
        return ['address', 'postcode'];
      case 'upload':
        return q.boxes.map(function (box) { return box.fieldName; });
      case 'contact':
        return ['name', 'contact'];
      default:
        return [];
    }
  }

  /* ---------- Wizard ---------- */

  function Wizard(root) {
    this.root = root;
    this.config = readConfig(root);
    this.workerBaseUrl = (root.dataset.workerBaseUrl || '').replace(/\/$/, '');
    this.startUrl = root.dataset.startUrl || '/';
    this.steps = buildSteps(this.config.items);
    this.nameField = this.config.nameField || 'full_name';
    this.state = {};
    this.draftContactId = (readDraft() || {}).contactId || null;
    this.furthestIndex = 0;
    this.activeIndex = 0;
    this.touched = {};
    this.attempted = false;
    this.uploadErrors = {};
    this.uploadPreviews = {};
    this.verifiedEmail = null;
    this.verifiedPhone = null;
    this.serviceabilityToken = 0;

    var self = this;
    this.steps.forEach(function (step) {
      step.questions.forEach(function (q) {
        questionFields(q).forEach(function (field) { self.state[field] = null; });
      });
    });

    this.elements = {
      progress: root.querySelector('[data-voltx-cmh-progress]'),
      progressFill: root.querySelector('[data-voltx-cmh-progress-fill]'),
      progressLabel: root.querySelector('[data-voltx-cmh-progress-label]'),
      stage: root.querySelector('[data-voltx-cmh-stage]')
    };
  }

  Wizard.prototype.init = async function () {
    if (!this.elements.stage) return;
    if (!this.steps.length) {
      this.elements.stage.innerHTML = '<p class="voltx-cmh__loading body">Add a Step block and some question blocks to this section to build the wizard.</p>';
      return;
    }

    this.bindEvents();

    if (this.draftContactId && this.workerBaseUrl) await this.loadProgress();
    this.applyIncomingParams();

    var requested = parseInt(new URLSearchParams(window.location.search).get('step'), 10);
    var start = this.furthestIndex;
    if (!isNaN(requested) && requested >= 1 && requested - 1 <= this.furthestIndex) start = requested - 1;

    this.render(start, { focus: false });
  };

  /* ?postcode=&state=&serviceability= (and optional ?address=) from the
     homepage hero pre-fill the first Address question. A resumed draft wins. */
  Wizard.prototype.applyIncomingParams = function () {
    var params = new URLSearchParams(window.location.search);
    var postcode = params.get('postcode');
    var address = params.get('address');
    if (!postcode && !address) return;

    var q = this.findQuestion(function (item) { return item.type === 'address'; });
    if (!q) return;
    var existing = this.state[q.fieldName];
    if (existing && (existing.postcode || existing.address)) return;

    this.state[q.fieldName] = {
      address: address || '',
      postcode: postcode || '',
      state: params.get('state') || stateFromPostcode(postcode || ''),
      serviceability: params.get('serviceability') || null
    };
  };

  Wizard.prototype.findQuestion = function (test) {
    for (var s = 0; s < this.steps.length; s++) {
      for (var i = 0; i < this.steps[s].questions.length; i++) {
        if (test(this.steps[s].questions[i])) return this.steps[s].questions[i];
      }
    }
    return null;
  };

  Wizard.prototype.stepIndexForField = function (field) {
    for (var s = 0; s < this.steps.length; s++) {
      for (var i = 0; i < this.steps[s].questions.length; i++) {
        if (questionFields(this.steps[s].questions[i]).indexOf(field) !== -1) return s;
      }
    }
    return 0;
  };

  /* ---------- Persistence ---------- */

  Wizard.prototype.ensureDraftContact = async function () {
    if (this.draftContactId) return this.draftContactId;
    var response = await fetch(this.workerBaseUrl + '/draft', { method: 'POST' });
    var data = await response.json();
    if (!data.ok) throw new Error(data.error || 'Failed to create draft');
    this.draftContactId = data.contactId;
    writeDraft(this.draftContactId);
    return this.draftContactId;
  };

  Wizard.prototype.loadProgress = async function () {
    try {
      var response = await fetch(this.workerBaseUrl + '/contact?contactId=' + encodeURIComponent(this.draftContactId));
      var data = await response.json();
      if (!data || !data.found) return;
      if (data.progressData) {
        try {
          Object.assign(this.state, JSON.parse(data.progressData));
        } catch (parseError) {
          /* keep defaults */
        }
      }
      if (typeof data.progressStep === 'number' && data.progressStep >= 0) {
        this.furthestIndex = Math.min(data.progressStep, this.steps.length - 1);
      }
    } catch (error) {
      console.error('[Check My Home] Failed to load progress:', error);
    }
  };

  Wizard.prototype.saveProgress = async function () {
    if (!this.workerBaseUrl) return;
    try {
      var contactId = await this.ensureDraftContact();
      var response = await fetch(this.workerBaseUrl + '/contact', {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          contactId: contactId,
          progressStep: this.furthestIndex,
          progressData: JSON.stringify(this.state)
        })
      });
      if (!response.ok) throw new Error('Save failed with status ' + response.status);
    } catch (error) {
      console.error('[Check My Home] Failed to save progress:', error);
    }
  };

  /* ---------- Personalisation ---------- */

  Wizard.prototype.firstName = function () {
    var name = this.state[this.nameField];
    if (!name || name === SKIPPED) return '';
    return String(name).trim().split(/\s+/)[0] || '';
  };

  /* "[Name], where…" → "Jake, where…" — or "Where…" when no name was given */
  Wizard.prototype.personalize = function (text) {
    text = String(text || '');
    var first = this.firstName();
    if (first) return text.replace(/\[Name\]/g, first);
    var stripped = text.replace(/\[Name\],?\s*/g, '');
    return stripped.charAt(0).toUpperCase() + stripped.slice(1);
  };

  /* ---------- Visibility + validation ---------- */

  Wizard.prototype.isVisible = function (q) {
    if (!q.showWhenField) return true;
    var value = this.state[q.showWhenField];
    var wanted = String(q.showWhenValue || '').trim();
    if (Array.isArray(value)) return value.indexOf(wanted) !== -1;
    return value != null && String(value) === wanted;
  };

  /* Returns { errors: { slot: message }, pending: bool } */
  Wizard.prototype.validate = function (q) {
    var result = { errors: {}, pending: false };
    if (!this.isVisible(q)) return result;
    var value = q.fieldName ? this.state[q.fieldName] : null;

    if (q.type === 'choice') {
      var empty = value == null || value === '' || value === SKIPPED || (Array.isArray(value) && !value.length);
      if (q.required && empty) result.errors.main = q.errorMessage || 'Please select one.';
    }

    if (q.type === 'text') {
      var text = value == null || value === SKIPPED ? '' : String(value).trim();
      if (q.inputKind === 'number' && text === 'unknown') return result;
      if (!text) {
        if (q.required) result.errors.main = q.errorMessage || 'This field is required.';
      } else if (q.inputKind === 'email' && !isValidEmail(text)) {
        result.errors.main = q.errorMessage || 'Please enter a valid email.';
      } else if (q.inputKind === 'number' && isNaN(parseFloat(text))) {
        result.errors.main = q.errorMessage || 'Please enter a number.';
      }
    }

    if (q.type === 'address') {
      var addr = value || {};
      if (q.required && !String(addr.address || '').trim()) {
        result.errors.address = q.addressError || 'Please enter your address.';
      }
      var postcode = String(addr.postcode || '');
      var state = addr.state || stateFromPostcode(postcode);
      if (!postcode && !q.required) return result;
      if (!/^\d{4}$/.test(postcode) || !state) {
        result.errors.postcode = q.postcodeError || 'Please enter valid postcode';
      } else if (!isStateAllowed(state, this.config.allowedStates)) {
        result.errors.postcode = q.outOfStateMessage || 'We’re not yet operating in this state.';
      } else if (addr.serviceability === 'checking') {
        result.pending = true;
      } else if (addr.serviceability === 'Not Serviceable' && !q.allowContinueRegardless) {
        result.errors.postcode = q.notServiceableMessage || 'We don’t currently service this area.';
      }
    }

    if (q.type === 'upload') {
      var self = this;
      q.boxes.forEach(function (box) {
        var file = self.state[box.fieldName];
        if (file && file.status === 'uploading') result.pending = true;
        /* Upload errors always show (see refresh), but only block when photos are required */
        if (q.required && !(file && file.status === 'success')) {
          result.errors[box.fieldName] = self.uploadErrors[box.fieldName] || q.requiredMessage || 'Please add this photo.';
        }
      });
    }

    if (q.type === 'contact') {
      var name = String(this.state[q.nameField] || '').trim();
      if (!name || name === SKIPPED) result.errors.name = q.nameError || 'Please enter your name.';
      var contact = String(this.state[q.fieldName] || '').trim();
      if (!contact || !(isValidEmail(contact) || isValidPhone(contact))) {
        result.errors.contact = q.contactError || 'Please enter a valid mobile number or email.';
      }
    }

    return result;
  };

  /* Re-evaluates the active step: hides/shows conditional questions, shows
     errors for fields the visitor has left (or all of them after a Continue
     attempt) and sets the Continue button's state. */
  Wizard.prototype.refresh = function () {
    var self = this;
    var step = this.steps[this.activeIndex];
    var stage = this.elements.stage;
    var complete = true;

    step.questions.forEach(function (q, qIndex) {
      var wrapper = stage.querySelector('[data-q="' + qIndex + '"]');
      if (!wrapper) return;
      var visible = self.isVisible(q);
      wrapper.hidden = !visible;

      var check = self.validate(q);
      if (check.pending) complete = false;

      questionSlots(q).forEach(function (slot) {
        var key = qIndex + '-' + slot;
        var blocking = check.errors[slot] || '';
        var notice = q.type === 'upload' && self.isVisible(q) ? self.uploadErrors[slot] || '' : '';
        var message = blocking || notice;
        if (blocking) complete = false;
        var show = !!message && (self.attempted || self.touched[key] || !!notice);

        var errorEl = stage.querySelector('[data-error-slot="' + key + '"]');
        if (errorEl) {
          errorEl.hidden = !show;
          var textEl = errorEl.querySelector('[data-error-text]');
          if (textEl) textEl.textContent = show ? message : '';
        }

        stage.querySelectorAll('[data-slot="' + key + '"]').forEach(function (input) {
          if (show) {
            input.setAttribute('aria-invalid', 'true');
            input.setAttribute('aria-describedby', errorId(key));
          } else {
            input.removeAttribute('aria-invalid');
          }
        });

        var group = stage.querySelector('[data-group="' + key + '"]');
        if (group) group.classList.toggle('is-error', show);
      });
    });

    var continueButton = stage.querySelector('[data-continue]');
    if (continueButton) {
      continueButton.classList.toggle('voltx-cmh__continue--incomplete', !complete);
      continueButton.setAttribute('aria-disabled', String(!complete));
    }
    return complete;
  };

  /* ---------- Rendering ---------- */

  Wizard.prototype.headingHtml = function (q, qIndex) {
    if (!q.heading) return '';
    var marker = q.required
      ? ' <span class="voltx-cmh__required" aria-hidden="true">*</span>'
      : (q.optionalLabel ? ' <span class="voltx-cmh__optional caption">' + escapeHtml(q.optionalLabel) + '</span>' : '');
    return (
      '<p class="voltx-cmh__q-heading h5" id="VoltxCmhQ-' + qIndex + '">' + escapeHtml(this.personalize(q.heading)) + marker + '</p>' +
      (q.description ? '<p class="voltx-cmh__q-desc caption">' + escapeHtml(this.personalize(q.description)) + '</p>' : '')
    );
  };

  Wizard.prototype.inputHtml = function (key, options) {
    var id = 'VoltxCmhInput-' + key;
    if (options.icon && !ICONS[options.icon]) options.icon = null;
    return (
      '<div class="voltx-cmh__field' + (options.className ? ' ' + options.className : '') + '">' +
      (options.label ? '<label class="voltx-cmh__field-label caption" for="' + id + '">' + escapeHtml(options.label) + '</label>' : '') +
      '<div class="voltx-cmh__control' + (options.icon ? ' voltx-cmh__control--icon' : '') + (options.unit ? ' voltx-cmh__control--unit' : '') + '">' +
      (options.icon && ICONS[options.icon] ? '<span class="voltx-cmh__control-icon">' + ICONS[options.icon] + '</span>' : '') +
      '<input class="voltx-cmh__input caption" id="' + id + '" type="' + (options.type || 'text') + '"' +
      ' data-slot="' + key + '" data-input="' + (options.role || 'text') + '"' +
      (options.placeholder ? ' placeholder="' + escapeHtml(options.placeholder) + '"' : '') +
      (options.inputmode ? ' inputmode="' + options.inputmode + '"' : '') +
      (options.maxlength ? ' maxlength="' + options.maxlength + '"' : '') +
      (options.autocomplete ? ' autocomplete="' + options.autocomplete + '"' : '') +
      (options.disabled ? ' disabled' : '') +
      ' value="' + escapeHtml(options.value || '') + '">' +
      (options.unit ? '<span class="voltx-cmh__control-unit caption">' + escapeHtml(options.unit) + '</span>' : '') +
      '</div>' +
      (options.after || '') +
      errorSlot(key) +
      '</div>'
    );
  };

  Wizard.prototype.questionHtml = function (q, qIndex) {
    var self = this;
    var value = q.fieldName ? this.state[q.fieldName] : null;
    var html = '';

    switch (q.type) {
      case 'choice': {
        var isCheckbox = q.inputType === 'checkbox';
        var selected = isCheckbox ? (Array.isArray(value) ? value : []) : value;
        var key = qIndex + '-main';
        html += this.headingHtml(q, qIndex);
        html +=
          '<div class="voltx-cmh__choices' + (String(q.columns) === '2' ? ' voltx-cmh__choices--cols-2' : '') + '"' +
          ' role="' + (isCheckbox ? 'group' : 'radiogroup') + '"' +
          (q.heading ? ' aria-labelledby="VoltxCmhQ-' + qIndex + '"' : '') +
          ' data-group="' + key + '">' +
          q.options.map(function (option) {
            var checked = isCheckbox ? selected.indexOf(option.value) !== -1 : selected === option.value;
            return (
              '<label class="voltx-cmh__choice voltx-cmh__choice--' + (isCheckbox ? 'checkbox' : 'radio') + '">' +
              '<input type="' + (isCheckbox ? 'checkbox' : 'radio') + '" class="voltx-cmh__choice-input" name="cmh-' + escapeHtml(q.fieldName) + '"' +
              ' value="' + escapeHtml(option.value) + '" data-choice="' + qIndex + '" data-slot="' + key + '"' + (checked ? ' checked' : '') + '>' +
              '<span class="voltx-cmh__choice-mark" aria-hidden="true"></span>' +
              '<span class="voltx-cmh__choice-label caption">' + escapeHtml(option.label) + '</span>' +
              '</label>'
            );
          }).join('') +
          '</div>' +
          errorSlot(key) +
          (q.note ? '<p class="voltx-cmh__q-note caption">' + escapeHtml(this.personalize(q.note)) + '</p>' : '');
        break;
      }

      case 'text': {
        var isNumber = q.inputKind === 'number';
        var isUnknown = isNumber && value === 'unknown';
        var textValue = value == null || value === SKIPPED || isUnknown ? '' : value;
        var unknownHtml = isNumber && q.unknownLabel
          ? '<label class="voltx-cmh__choice voltx-cmh__choice--radio voltx-cmh__choice--inline">' +
            '<input type="checkbox" class="voltx-cmh__choice-input" data-unknown="' + qIndex + '"' + (isUnknown ? ' checked' : '') + '>' +
            '<span class="voltx-cmh__choice-mark" aria-hidden="true"></span>' +
            '<span class="voltx-cmh__choice-label caption">' + escapeHtml(q.unknownLabel) + '</span>' +
            '</label>'
          : '';
        html += this.headingHtml(q, qIndex);
        html += this.inputHtml(qIndex + '-main', {
          label: q.label,
          placeholder: q.placeholder,
          icon: q.icon,
          unit: q.unit,
          value: textValue,
          disabled: isUnknown,
          type: q.inputKind === 'email' ? 'email' : q.inputKind === 'tel' ? 'tel' : 'text',
          inputmode: isNumber ? 'decimal' : q.inputKind === 'tel' ? 'tel' : null,
          autocomplete: q.inputKind === 'name' ? 'name' : q.inputKind === 'email' ? 'email' : q.inputKind === 'tel' ? 'tel' : 'off',
          role: 'text',
          className: unknownHtml ? 'voltx-cmh__field--with-unknown' : '',
          after: unknownHtml
        });
        break;
      }

      case 'address': {
        var addr = value || {};
        html += this.headingHtml(q, qIndex);
        html += this.inputHtml(qIndex + '-address', {
          label: q.addressLabel,
          placeholder: q.addressPlaceholder,
          icon: 'home',
          value: addr.address,
          autocomplete: 'off',
          role: 'address'
        });
        html += this.inputHtml(qIndex + '-postcode', {
          label: q.postcodeLabel,
          placeholder: q.postcodePlaceholder,
          icon: 'pin',
          value: addr.postcode,
          inputmode: 'numeric',
          maxlength: 4,
          autocomplete: 'postal-code',
          role: 'postcode',
          after: '<p class="voltx-cmh__note caption" data-postcode-note hidden></p>'
        });
        break;
      }

      case 'upload': {
        html += this.headingHtml(q, qIndex);
        html += '<div class="voltx-cmh__uploads">' + q.boxes.map(function (box) {
          return (
            '<div class="voltx-cmh__upload-wrap">' +
            '<div class="voltx-cmh__upload" data-upload-box="' + escapeHtml(box.fieldName) + '" data-q-index="' + qIndex + '" data-group="' + qIndex + '-' + escapeHtml(box.fieldName) + '">' +
            self.uploadBoxInner(q, qIndex, box) +
            '</div>' +
            errorSlot(qIndex + '-' + box.fieldName) +
            '</div>'
          );
        }).join('') + '</div>';
        break;
      }

      case 'media': {
        var mediaInner =
          (q.image ? '<img src="' + escapeHtml(q.image) + '" alt="" class="voltx-cmh__media-image" loading="lazy">' : '') +
          '<span class="voltx-cmh__media-body">' +
          '<span class="voltx-cmh__media-title caption">' + escapeHtml(q.title) + '</span>' +
          (q.linkLabel ? '<span class="voltx-cmh__media-link caption">' + escapeHtml(q.linkLabel) + '</span>' : '') +
          '</span>';
        html += q.url
          ? '<a class="voltx-cmh__media" href="' + escapeHtml(q.url) + '" target="_blank" rel="noopener">' + mediaInner + '</a>'
          : '<div class="voltx-cmh__media">' + mediaInner + '</div>';
        break;
      }

      case 'review': {
        html += this.headingHtml(q, qIndex);
        html += this.reviewHtml(q);
        break;
      }

      case 'contact': {
        var consentChecked = this.state[q.consentField];
        if (consentChecked == null) consentChecked = !!q.consentDefault;
        html += this.headingHtml(q, qIndex);
        html += '<div class="voltx-cmh__contact-grid">';
        html += this.inputHtml(qIndex + '-name', {
          label: q.nameLabel,
          placeholder: q.namePlaceholder,
          icon: 'user',
          value: this.state[q.nameField] === SKIPPED ? '' : this.state[q.nameField],
          autocomplete: 'name',
          role: 'contact-name'
        });
        html += this.inputHtml(qIndex + '-contact', {
          label: q.contactLabel,
          placeholder: q.contactPlaceholder,
          icon: 'phone',
          value: this.state[q.fieldName],
          autocomplete: 'email',
          role: 'contact'
        });
        html += '</div>';
        if (q.note) html += '<p class="voltx-cmh__contact-note caption">' + escapeHtml(q.note) + '</p>';
        if (q.consentLabel && q.consentField) {
          html +=
            '<label class="voltx-cmh__choice voltx-cmh__choice--checkbox voltx-cmh__consent">' +
            '<input type="checkbox" class="voltx-cmh__choice-input" data-consent="' + qIndex + '"' + (consentChecked ? ' checked' : '') + '>' +
            '<span class="voltx-cmh__choice-mark" aria-hidden="true"></span>' +
            '<span class="voltx-cmh__choice-label caption">' + escapeHtml(q.consentLabel) + '</span>' +
            '</label>';
          this.state[q.consentField] = !!consentChecked;
        }
        break;
      }
    }

    return '<div class="voltx-cmh__q voltx-cmh__q--' + q.type + '" data-q="' + qIndex + '">' + html + '</div>';
  };

  Wizard.prototype.uploadBoxInner = function (q, qIndex, box) {
    var file = this.state[box.fieldName];
    var status = file && file.status ? file.status : 'empty';
    var inputId = 'VoltxCmhFile-' + box.fieldName;
    var title = '<p class="voltx-cmh__upload-title caption">' + escapeHtml(box.title) + '</p>';

    if (status === 'uploading') {
      return title +
        '<span class="voltx-cmh__spinner" aria-hidden="true"></span>' +
        '<p class="voltx-cmh__upload-text caption" role="status">' + escapeHtml(q.uploadingLabel || 'Uploading…') + ' ' + escapeHtml(file.fileName || '') + '</p>';
    }

    if (status === 'success') {
      var preview = this.uploadPreviews[box.fieldName];
      return title +
        (preview
          ? '<img src="' + escapeHtml(preview) + '" alt="" class="voltx-cmh__upload-thumb">'
          : '<span class="voltx-cmh__upload-thumb voltx-cmh__upload-thumb--icon">' + ICONS.image + '</span>') +
        '<p class="voltx-cmh__upload-text caption"><span class="voltx-cmh__upload-name">' + escapeHtml(file.fileName || '') + '</span> ' +
        '<button type="button" class="voltx-cmh__upload-remove" data-upload-remove="' + escapeHtml(box.fieldName) + '" data-q-index="' + qIndex + '">' + escapeHtml(q.removeLabel || 'Remove') + '</button></p>' +
        '<p class="voltx-cmh__upload-done caption">' + ICONS.check + escapeHtml(q.uploadedLabel || 'Uploaded') + '</p>';
    }

    return title +
      '<span class="voltx-cmh__upload-icon">' + ICONS.upload + '</span>' +
      '<p class="voltx-cmh__upload-text caption">' + escapeHtml(q.dragLabel || 'Drag your file(s) or') + ' ' +
      '<label for="' + inputId + '" class="voltx-cmh__upload-browse">' + escapeHtml(q.browseLabel || 'browse') + '</label></p>' +
      '<input type="file" id="' + inputId + '" class="voltx-cmh__upload-input" accept="' + escapeHtml(q.accept || 'image/jpeg,image/png') + '"' +
      ' data-upload-input="' + escapeHtml(box.fieldName) + '" data-q-index="' + qIndex + '" data-slot="' + qIndex + '-' + escapeHtml(box.fieldName) + '">' +
      (q.hint ? '<p class="voltx-cmh__upload-hint caption">' + escapeHtml(q.hint).replace(/\n/g, '<br>') + '</p>' : '');
  };

  Wizard.prototype.displayValue = function (q, notAnswered) {
    var value = q.fieldName ? this.state[q.fieldName] : null;
    if (q.type === 'choice') {
      var list = Array.isArray(value) ? value : value != null && value !== '' && value !== SKIPPED ? [value] : [];
      var labels = list.map(function (v) {
        var match = q.options.filter(function (option) { return option.value === v; })[0];
        return match ? match.label : v;
      });
      return labels.length ? labels.join(', ') : notAnswered;
    }
    if (q.type === 'text') {
      if (value === 'unknown') return q.unknownLabel || 'Unknown';
      if (value == null || value === '' || value === SKIPPED) return notAnswered;
      return String(value) + (q.unit ? ' ' + q.unit : '');
    }
    if (q.type === 'address') {
      var addr = value || {};
      return addr.address || addr.postcode || notAnswered;
    }
    if (q.type === 'upload') {
      var self = this;
      var count = q.boxes.filter(function (box) {
        var file = self.state[box.fieldName];
        return file && file.status === 'success';
      }).length;
      return count ? count + ' of ' + q.boxes.length + ' added' : notAnswered;
    }
    return notAnswered;
  };

  Wizard.prototype.reviewHtml = function (q) {
    var self = this;
    var notAnswered = q.notAnsweredLabel || 'Not answered';
    var rows = [];
    this.steps.forEach(function (step, stepIndex) {
      if (stepIndex === self.activeIndex) return;
      step.questions.forEach(function (item) {
        if (!item.reviewLabel) return;
        rows.push(
          '<div class="voltx-cmh__review-item">' +
          '<dt class="voltx-cmh__review-label caption">' + escapeHtml(item.reviewLabel) + '</dt>' +
          '<dd class="voltx-cmh__review-value caption">' + escapeHtml(self.displayValue(item, notAnswered)) + '</dd>' +
          '<button type="button" class="voltx-cmh__review-edit caption" data-edit-step="' + stepIndex + '">' +
          escapeHtml(q.editLabel || 'Edit') + '<span class="visually-hidden"> ' + escapeHtml(item.reviewLabel) + '</span></button>' +
          '</div>'
        );
      });
    });

    return (
      '<div class="voltx-cmh__review">' +
      '<div class="voltx-cmh__review-head">' +
      '<p class="voltx-cmh__review-title caption">' + escapeHtml(q.cardTitle || 'Your details') + '</p>' +
      (q.editAllLabel ? '<button type="button" class="voltx-cmh__review-edit caption" data-edit-step="0">' + escapeHtml(q.editAllLabel) + '</button>' : '') +
      '</div>' +
      (rows.length ? '<dl class="voltx-cmh__review-grid">' + rows.join('') + '</dl>' : '') +
      '</div>'
    );
  };

  Wizard.prototype.navHtml = function (step) {
    var labels = this.config.labels || {};
    return (
      '<div class="voltx-cmh__nav">' +
      '<button type="button" class="voltx-cmh__back body" data-back>' +
      '<span class="voltx-cmh__nav-icon voltx-cmh__nav-icon--back">' + ICONS.arrow + '</span>' +
      escapeHtml(labels.back || 'Back') +
      '</button>' +
      '<div class="voltx-cmh__nav-right">' +
      (step.showSkip
        ? '<button type="button" class="btn btn--secondary voltx-cmh__skip" data-skip>' + escapeHtml(labels.skip || 'Skip') +
          '<span class="voltx-cmh__nav-icon">' + ICONS.arrow + '</span></button>'
        : '') +
      '<button type="button" class="btn btn--primary voltx-cmh__continue" data-continue>' + escapeHtml(labels.continue || 'Continue') +
      '<span class="voltx-cmh__nav-icon voltx-cmh__nav-icon--light">' + ICONS.arrow + '</span></button>' +
      '</div>' +
      '</div>'
    );
  };

  Wizard.prototype.mediaHtml = function (step) {
    var image = step.image;
    if (!image || !image.src) return '<div class="voltx-cmh__visual voltx-cmh__visual--empty" aria-hidden="true"></div>';
    return (
      '<div class="voltx-cmh__visual">' +
      '<img class="voltx-cmh__visual-image" src="' + escapeHtml(image.src) + '"' +
      (image.srcset ? ' srcset="' + escapeHtml(image.srcset) + '" sizes="(min-width: 990px) 45vw, 100vw"' : '') +
      ' alt="' + escapeHtml(image.alt || '') + '"' +
      (image.width ? ' width="' + image.width + '" height="' + image.height + '"' : '') +
      '>' +
      '</div>'
    );
  };

  Wizard.prototype.updateProgress = function (index) {
    var step = this.steps[index];
    var labels = this.config.labels || {};
    if (this.elements.progress) this.elements.progress.hidden = false;
    if (this.elements.progressFill) {
      this.elements.progressFill.style.width = ((index + 1) / this.steps.length * 100) + '%';
    }
    if (this.elements.progressLabel) {
      this.elements.progressLabel.innerHTML =
        '<strong>' + escapeHtml((labels.step || 'Step') + ' ' + (index + 1)) + ' :</strong> ' + escapeHtml(step.title);
    }
    if (this.elements.progress) {
      this.elements.progress.setAttribute('aria-valuenow', String(index + 1));
      this.elements.progress.setAttribute('aria-valuemax', String(this.steps.length));
    }
  };

  Wizard.prototype.render = function (index, options) {
    options = options || {};
    var step = this.steps[index];
    this.activeIndex = index;
    this.touched = {};
    this.attempted = false;
    this.updateProgress(index);

    var self = this;
    this.elements.stage.innerHTML =
      '<div class="voltx-cmh__layout">' +
      this.mediaHtml(step) +
      '<div class="voltx-cmh__panel">' +
      '<div class="voltx-cmh__questions">' +
      step.questions.map(function (q, qIndex) { return self.questionHtml(q, qIndex); }).join('') +
      '</div>' +
      this.navHtml(step) +
      '</div>' +
      '</div>';

    this.refresh();
    this.setupAddressAutocomplete();
    this.updatePostcodeNotes();

    if (options.focus !== false) {
      var top = this.root.getBoundingClientRect().top;
      if (top < 0) this.root.scrollIntoView({ block: 'start', behavior: 'smooth' });
      var firstHeading = this.elements.stage.querySelector('.voltx-cmh__q-heading');
      if (firstHeading) {
        firstHeading.setAttribute('tabindex', '-1');
        firstHeading.focus({ preventScroll: true });
      }
    }
  };

  Wizard.prototype.goTo = function (index, options) {
    options = options || {};
    var clamped = Math.max(0, Math.min(index, this.steps.length - 1));
    if (clamped > this.furthestIndex) this.furthestIndex = clamped;
    var url = new URL(window.location.href);
    url.searchParams.set('step', String(clamped + 1));
    window.history.replaceState({}, '', url);
    this.render(clamped);
    if (options.save) this.saveProgress();
  };

  /* ---------- Address ---------- */

  Wizard.prototype.addressQuestions = function () {
    var step = this.steps[this.activeIndex];
    var list = [];
    step.questions.forEach(function (q, qIndex) {
      if (q.type === 'address') list.push({ q: q, qIndex: qIndex });
    });
    return list;
  };

  Wizard.prototype.setupAddressAutocomplete = function () {
    var self = this;
    var list = this.addressQuestions();
    if (!list.length || !this.config.googlePlacesApiKey) return;

    loadGoogleMaps(this.config.googlePlacesApiKey).then(function () {
      list.forEach(function (entry) {
        var input = self.elements.stage.querySelector('[data-slot="' + entry.qIndex + '-address"]');
        if (!input) return;
        var autocomplete = new google.maps.places.Autocomplete(input, {
          componentRestrictions: { country: 'au' },
          fields: ['address_components', 'formatted_address']
        });
        autocomplete.addListener('place_changed', function () {
          var place = autocomplete.getPlace();
          if (!place || !place.address_components) return;
          var find = function (type) {
            return place.address_components.filter(function (c) { return c.types.indexOf(type) !== -1; })[0];
          };
          var postcodeComponent = find('postal_code');
          var stateComponent = find('administrative_area_level_1');
          var value = self.state[entry.q.fieldName] || {};
          value.address = place.formatted_address || input.value;
          input.value = value.address;
          if (postcodeComponent) {
            value.postcode = postcodeComponent.long_name;
            var postcodeInput = self.elements.stage.querySelector('[data-slot="' + entry.qIndex + '-postcode"]');
            if (postcodeInput) postcodeInput.value = value.postcode;
          }
          value.state = stateComponent ? stateComponent.short_name : stateFromPostcode(value.postcode);
          self.state[entry.q.fieldName] = value;
          self.touched[entry.qIndex + '-address'] = true;
          self.touched[entry.qIndex + '-postcode'] = true;
          self.runServiceability(entry.q);
        });
      });
    }).catch(function (err) {
      console.error('[Check My Home] Google Maps failed to load:', err);
    });
  };

  Wizard.prototype.runServiceability = async function (q) {
    var value = this.state[q.fieldName] || {};
    this.state[q.fieldName] = value;
    var postcode = String(value.postcode || '');
    var token = ++this.serviceabilityToken;

    if (!/^\d{4}$/.test(postcode) || !value.state || !isStateAllowed(value.state, this.config.allowedStates) || !this.config.postcodeApiUrl) {
      value.serviceability = null;
      this.refresh();
      this.updatePostcodeNotes();
      return;
    }

    value.serviceability = 'checking';
    this.refresh();
    this.updatePostcodeNotes();

    var result;
    try {
      result = (await checkPostcodeServiceability(this.config.postcodeApiUrl, postcode)) || 'not_found';
    } catch (error) {
      console.error('[Check My Home] Postcode lookup failed:', error);
      result = 'error';
    }
    if (token !== this.serviceabilityToken) return; /* a newer postcode is being checked */
    value.serviceability = result;
    this.refresh();
    this.updatePostcodeNotes();
  };

  /* Short status under the postcode (errors themselves use the error slot) */
  Wizard.prototype.updatePostcodeNotes = function () {
    var self = this;
    this.addressQuestions().forEach(function (entry) {
      var wrapper = self.elements.stage.querySelector('[data-q="' + entry.qIndex + '"]');
      var note = wrapper && wrapper.querySelector('[data-postcode-note]');
      if (!note) return;
      var q = entry.q;
      var value = self.state[q.fieldName] || {};
      var text = '';
      var modifier = '';
      var hasError = !!self.validate(q).errors.postcode;
      if (!hasError) {
        if (value.serviceability === 'checking') { text = q.checkingMessage || 'Checking your area…'; modifier = 'neutral'; }
        else if (value.serviceability === 'Serviceable') { text = q.serviceableMessage || ''; modifier = 'ok'; }
        else if (value.serviceability === 'Approval Required') { text = q.approvalMessage || ''; modifier = 'warn'; }
        else if (value.serviceability === 'Not Serviceable') { text = q.notServiceableMessage || ''; modifier = 'warn'; }
        else if (value.serviceability === 'not_found') { text = q.notFoundMessage || ''; modifier = 'neutral'; }
        else if (value.serviceability === 'error') { text = q.lookupErrorMessage || ''; modifier = 'neutral'; }
      }
      note.hidden = !text;
      note.textContent = text;
      note.className = 'voltx-cmh__note caption' + (modifier ? ' voltx-cmh__note--' + modifier : '');
    });
  };

  /* ---------- Uploads ---------- */

  Wizard.prototype.uploadQuestion = function (qIndex) {
    return this.steps[this.activeIndex].questions[qIndex];
  };

  Wizard.prototype.renderUploadBox = function (q, qIndex, fieldName) {
    var boxEl = this.elements.stage.querySelector('[data-upload-box="' + fieldName + '"]');
    var box = q.boxes.filter(function (item) { return item.fieldName === fieldName; })[0];
    if (!boxEl || !box) return;
    var file = this.state[fieldName];
    boxEl.setAttribute('data-status', file && file.status ? file.status : 'empty');
    boxEl.innerHTML = this.uploadBoxInner(q, qIndex, box);
    this.refresh();
  };

  Wizard.prototype.handleFile = async function (qIndex, fieldName, file) {
    var q = this.uploadQuestion(qIndex);
    if (!q || !file) return;
    var maxBytes = (parseFloat(q.maxMb) || 10) * 1024 * 1024;
    var accepted = String(q.accept || 'image/jpeg,image/png').split(',').map(function (t) { return t.trim().toLowerCase(); });
    var extension = (file.name.split('.').pop() || '').toLowerCase();
    var typeOk = accepted.some(function (type) {
      if (type.charAt(0) === '.') return type.slice(1) === extension;
      if (type.slice(-2) === '/*') return file.type.indexOf(type.slice(0, -1)) === 0;
      return file.type === type || (type === 'image/jpeg' && (extension === 'jpg' || extension === 'jpeg')) || (type === 'image/png' && extension === 'png');
    });

    this.touched[qIndex + '-' + fieldName] = true;

    if (!typeOk) {
      this.uploadErrors[fieldName] = q.typeError || 'This file type isn’t supported. Upload a JPG or PNG file.';
      this.renderUploadBox(q, qIndex, fieldName);
      return;
    }
    if (file.size > maxBytes) {
      this.uploadErrors[fieldName] = q.sizeError || 'File is too large. Choose a file under 10 MB.';
      this.renderUploadBox(q, qIndex, fieldName);
      return;
    }

    delete this.uploadErrors[fieldName];
    if (this.uploadPreviews[fieldName]) URL.revokeObjectURL(this.uploadPreviews[fieldName]);
    this.uploadPreviews[fieldName] = URL.createObjectURL(file);
    this.state[fieldName] = { status: 'uploading', fileName: file.name };
    this.renderUploadBox(q, qIndex, fieldName);

    try {
      if (!this.workerBaseUrl) throw new Error('Worker base URL is not set');
      var formData = new FormData();
      formData.append('file', file);
      formData.append('options', JSON.stringify({ access: 'PRIVATE' }));
      formData.append('folderPath', '/check-my-home-photos');
      var response = await fetch(this.workerBaseUrl + '/upload-photo', { method: 'POST', body: formData });
      var data = await response.json();
      if (!response.ok || !data.url) throw new Error('Upload failed');
      this.state[fieldName] = { status: 'success', fileName: file.name, fileUrl: data.url };
    } catch (error) {
      console.error('[Check My Home] Upload failed:', error);
      this.state[fieldName] = null;
      this.uploadErrors[fieldName] = q.failedError || 'Upload didn’t finish. Check your connection and try again.';
    }
    this.renderUploadBox(q, qIndex, fieldName);
  };

  /* ---------- Events (delegated on the stage) ---------- */

  Wizard.prototype.bindEvents = function () {
    var self = this;
    var stage = this.elements.stage;

    stage.addEventListener('change', function (event) {
      var target = event.target;
      var step = self.steps[self.activeIndex];

      if (target.hasAttribute('data-choice')) {
        var qIndex = parseInt(target.getAttribute('data-choice'), 10);
        var q = step.questions[qIndex];
        if (q.inputType === 'checkbox') {
          var checked = Array.prototype.slice.call(stage.querySelectorAll('[data-choice="' + qIndex + '"]:checked'));
          self.state[q.fieldName] = checked.map(function (input) { return input.value; });
        } else {
          self.state[q.fieldName] = target.value;
        }
        self.touched[qIndex + '-main'] = true;
        self.refresh();
        return;
      }

      if (target.hasAttribute('data-unknown')) {
        var uIndex = parseInt(target.getAttribute('data-unknown'), 10);
        var uq = step.questions[uIndex];
        var numberInput = stage.querySelector('[data-slot="' + uIndex + '-main"]');
        self.state[uq.fieldName] = target.checked ? 'unknown' : '';
        if (numberInput) {
          numberInput.disabled = target.checked;
          if (target.checked) numberInput.value = '';
        }
        self.touched[uIndex + '-main'] = true;
        self.refresh();
        return;
      }

      if (target.hasAttribute('data-consent')) {
        var cq = step.questions[parseInt(target.getAttribute('data-consent'), 10)];
        self.state[cq.consentField] = target.checked;
        return;
      }

      if (target.hasAttribute('data-upload-input') && target.files && target.files[0]) {
        self.handleFile(parseInt(target.getAttribute('data-q-index'), 10), target.getAttribute('data-upload-input'), target.files[0]);
      }
    });

    stage.addEventListener('input', function (event) {
      var target = event.target;
      var role = target.getAttribute('data-input');
      if (!role) return;
      var key = target.getAttribute('data-slot') || '';
      var qIndex = parseInt(key, 10);
      var q = self.steps[self.activeIndex].questions[qIndex];
      if (!q) return;

      if (role === 'text') {
        if (q.inputKind === 'number') {
          var cleaned = target.value.replace(/[^\d.]/g, '');
          if (cleaned !== target.value) target.value = cleaned;
        }
        self.state[q.fieldName] = target.value;
      } else if (role === 'address' || role === 'postcode') {
        var value = self.state[q.fieldName] || {};
        self.state[q.fieldName] = value;
        if (role === 'address') {
          value.address = target.value;
        } else {
          var digits = target.value.replace(/\D/g, '').slice(0, 4);
          if (digits !== target.value) target.value = digits;
          value.postcode = digits;
          value.state = stateFromPostcode(digits);
          value.serviceability = null;
          if (digits.length === 4) {
            self.touched[key] = true;
            self.runServiceability(q);
            return;
          }
          self.updatePostcodeNotes();
        }
      } else if (role === 'contact-name') {
        self.state[q.nameField] = target.value;
      } else if (role === 'contact') {
        self.state[q.fieldName] = target.value.trim();
      }
      self.refresh();
    });

    stage.addEventListener('focusout', function (event) {
      var key = event.target.getAttribute && event.target.getAttribute('data-slot');
      if (!key || !event.target.hasAttribute('data-input')) return;
      self.touched[key] = true;
      self.refresh();
      self.updatePostcodeNotes();
    });

    stage.addEventListener('click', function (event) {
      var target = event.target.closest('button');
      if (!target) return;

      if (target.hasAttribute('data-back')) {
        if (self.activeIndex === 0) window.location.href = self.startUrl;
        else self.goTo(self.activeIndex - 1);
        return;
      }
      if (target.hasAttribute('data-skip')) {
        self.skipStep();
        return;
      }
      if (target.hasAttribute('data-continue')) {
        self.continueStep(target);
        return;
      }
      if (target.hasAttribute('data-edit-step')) {
        self.goTo(parseInt(target.getAttribute('data-edit-step'), 10));
        return;
      }
      if (target.hasAttribute('data-upload-remove')) {
        var fieldName = target.getAttribute('data-upload-remove');
        var qIndex = parseInt(target.getAttribute('data-q-index'), 10);
        self.state[fieldName] = null;
        delete self.uploadErrors[fieldName];
        if (self.uploadPreviews[fieldName]) {
          URL.revokeObjectURL(self.uploadPreviews[fieldName]);
          delete self.uploadPreviews[fieldName];
        }
        self.renderUploadBox(self.uploadQuestion(qIndex), qIndex, fieldName);
      }
    });

    ['dragenter', 'dragover'].forEach(function (type) {
      stage.addEventListener(type, function (event) {
        var box = event.target.closest && event.target.closest('[data-upload-box]');
        if (!box) return;
        event.preventDefault();
        box.classList.add('is-dragover');
      });
    });

    ['dragleave', 'drop'].forEach(function (type) {
      stage.addEventListener(type, function (event) {
        var box = event.target.closest && event.target.closest('[data-upload-box]');
        if (!box) return;
        event.preventDefault();
        box.classList.remove('is-dragover');
        if (type === 'drop' && event.dataTransfer && event.dataTransfer.files && event.dataTransfer.files[0]) {
          self.handleFile(parseInt(box.getAttribute('data-q-index'), 10), box.getAttribute('data-upload-box'), event.dataTransfer.files[0]);
        }
      });
    });
  };

  /* Skip: record every unanswered visible question as "unanswered" (never a
     silent assumption), then move on. */
  Wizard.prototype.skipStep = function () {
    var self = this;
    this.steps[this.activeIndex].questions.forEach(function (q) {
      if (!self.isVisible(q) || !q.fieldName) return;
      var value = self.state[q.fieldName];
      if (q.type === 'choice' || q.type === 'text') {
        var empty = value == null || value === '' || (Array.isArray(value) && !value.length);
        if (empty) self.state[q.fieldName] = SKIPPED;
      }
    });
    if (this.activeIndex >= this.steps.length - 1) {
      this.finish();
      return;
    }
    this.goTo(this.activeIndex + 1, { save: true });
  };

  Wizard.prototype.continueStep = function (button) {
    this.attempted = true;
    var complete = this.refresh();
    this.updatePostcodeNotes();
    if (!complete) {
      var firstInvalid = this.elements.stage.querySelector('[aria-invalid="true"]');
      if (firstInvalid) firstInvalid.focus();
      return;
    }

    var contactQ = this.steps[this.activeIndex].questions.filter(function (q) { return q.type === 'contact'; })[0];
    if (contactQ) {
      this.submitContact(contactQ, button);
      return;
    }
    if (this.activeIndex >= this.steps.length - 1) {
      this.finish();
      return;
    }
    this.goTo(this.activeIndex + 1, { save: true });
  };

  /* ---------- Verification (email or SMS code) ---------- */

  Wizard.prototype.submitContact = async function (q, button) {
    var contact = String(this.state[q.fieldName] || '').trim();
    var isEmail = isValidEmail(contact);
    var errorEl = this.elements.stage.querySelector('[data-error-slot="' + this.steps[this.activeIndex].questions.indexOf(q) + '-contact"]');

    if (!this.workerBaseUrl) {
      /* No Worker configured (e.g. local preview): skip verification */
      if (isEmail) this.verifiedEmail = contact;
      else this.verifiedPhone = '+' + toE164(contact);
      this.finish();
      return;
    }

    button.classList.add('is-loading');
    try {
      var contactId = await this.ensureDraftContact();
      await this.saveProgress();
      if (isEmail) {
        var emailResponse = await fetch(this.workerBaseUrl + '/otp/send', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ contactId: contactId, email: contact.toLowerCase() })
        });
        var emailData = await emailResponse.json();
        if (!emailData.ok) throw new Error(emailData.error || 'Failed to send code');
        if (emailData.contactId && emailData.contactId !== this.draftContactId) {
          /* Same email already has a contact from an earlier visit — adopt it
             so verify targets the contact holding the code, and move this
             visit's answers onto it. */
          this.draftContactId = emailData.contactId;
          writeDraft(this.draftContactId);
          this.saveProgress();
        }
        this.renderVerify({ channel: 'email', email: contact.toLowerCase(), masked: emailData.maskedEmail || contact });
      } else {
        var phone = toE164(contact);
        var smsResponse = await fetch(this.workerBaseUrl + '/otp/send-sms', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ contactId: contactId, phone: phone })
        });
        var smsData = await smsResponse.json();
        if (!smsData.ok) throw new Error(smsData.error || 'Failed to send SMS');
        this.renderVerify({ channel: 'sms', phone: smsData.cleanPhone || phone, masked: '+' + (smsData.cleanPhone || phone) });
      }
    } catch (error) {
      console.error('[Check My Home] Sending code failed:', error);
      button.classList.remove('is-loading');
      if (errorEl) {
        errorEl.hidden = false;
        errorEl.querySelector('[data-error-text]').textContent = (this.config.verify || {}).sendError || 'We couldn’t send a code — please check your details and try again.';
      }
    }
  };

  Wizard.prototype.renderVerify = function (data) {
    var self = this;
    var verify = this.config.verify || {};
    var length = data.channel === 'sms' ? 6 : 4;
    if (this.elements.progress) this.elements.progress.hidden = true;

    this.elements.stage.innerHTML =
      '<div class="voltx-cmh__card">' +
      '<p class="voltx-cmh__card-heading h4" tabindex="-1">' + escapeHtml(verify.heading || 'Verify your details') + '</p>' +
      '<p class="voltx-cmh__card-text body">' + escapeHtml(verify.text || 'We’ve sent a code to') + ' <strong>' + escapeHtml(data.masked) + '</strong></p>' +
      '<div class="voltx-cmh__otp" role="group" aria-label="Enter the ' + length + ' digit code">' +
      Array(length + 1).join('<input class="voltx-cmh__otp-input" type="text" inputmode="numeric" maxlength="1" autocomplete="one-time-code">') +
      '</div>' +
      '<p class="voltx-cmh__error caption" data-otp-error hidden>' + ICONS.error + '<span data-error-text></span></p>' +
      '<button type="button" class="btn btn--primary voltx-cmh__card-button" data-otp-verify>' + escapeHtml(verify.buttonLabel || 'Verify') +
      '<span class="voltx-cmh__nav-icon voltx-cmh__nav-icon--light">' + ICONS.arrow + '</span></button>' +
      '<p class="voltx-cmh__card-meta caption"><button type="button" class="voltx-cmh__text-button" data-otp-resend disabled>' + escapeHtml(verify.resendLabel || 'Resend code') + '</button>' +
      ' <span data-otp-timer></span></p>' +
      '<p class="voltx-cmh__card-meta caption"><button type="button" class="voltx-cmh__text-button" data-otp-change>' + escapeHtml(verify.changeLabel || 'Use a different email or mobile') + '</button></p>' +
      '</div>';

    var stage = this.elements.stage;
    var inputs = Array.prototype.slice.call(stage.querySelectorAll('.voltx-cmh__otp-input'));
    var verifyButton = stage.querySelector('[data-otp-verify]');
    var resendButton = stage.querySelector('[data-otp-resend]');
    var timerEl = stage.querySelector('[data-otp-timer]');
    var errorEl = stage.querySelector('[data-otp-error]');
    var timer = null;

    function code() {
      return inputs.map(function (input) { return input.value; }).join('');
    }

    function showError(text) {
      errorEl.hidden = !text;
      errorEl.querySelector('[data-error-text]').textContent = text || '';
      inputs.forEach(function (input) {
        if (text) input.setAttribute('aria-invalid', 'true');
        else input.removeAttribute('aria-invalid');
      });
    }

    function startTimer() {
      var remaining = 50;
      resendButton.disabled = true;
      clearInterval(timer);
      timerEl.textContent = '(0:' + remaining + ')';
      timer = setInterval(function () {
        remaining -= 1;
        timerEl.textContent = remaining > 0 ? '(0:' + String(remaining).padStart(2, '0') + ')' : '';
        if (remaining <= 0) {
          clearInterval(timer);
          resendButton.disabled = false;
        }
      }, 1000);
    }

    inputs.forEach(function (input, index) {
      input.addEventListener('input', function () {
        var digits = input.value.replace(/\D/g, '');
        if (digits.length > 1) {
          /* pasted the whole code */
          digits.split('').slice(0, length - index).forEach(function (digit, offset) {
            inputs[index + offset].value = digit;
          });
          inputs[Math.min(index + digits.length, length - 1)].focus();
        } else {
          input.value = digits;
          if (digits && index < length - 1) inputs[index + 1].focus();
        }
        showError('');
      });
      input.addEventListener('keydown', function (event) {
        if (event.key === 'Backspace' && !input.value && index > 0) inputs[index - 1].focus();
        if (event.key === 'Enter') verifyButton.click();
      });
    });

    resendButton.addEventListener('click', async function () {
      startTimer();
      try {
        if (data.channel === 'email') {
          await fetch(self.workerBaseUrl + '/otp/send', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ contactId: self.draftContactId, email: data.email })
          });
        } else {
          await fetch(self.workerBaseUrl + '/otp/send-sms', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ contactId: self.draftContactId, phone: data.phone })
          });
        }
      } catch (error) {
        console.error('[Check My Home] Resend failed:', error);
      }
    });

    stage.querySelector('[data-otp-change]').addEventListener('click', function () {
      clearInterval(timer);
      self.render(self.activeIndex);
    });

    verifyButton.addEventListener('click', async function () {
      if (code().length !== length) {
        showError(verify.incompleteError || 'Please enter the full code.');
        inputs[code().length] && inputs[code().length].focus();
        return;
      }
      verifyButton.classList.add('is-loading');
      try {
        var response = data.channel === 'email'
          ? await fetch(self.workerBaseUrl + '/otp/verify', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ contactId: self.draftContactId, code: code() })
            })
          : await fetch(self.workerBaseUrl + '/otp/verify-sms', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ phone: data.phone, code: code() })
            });
        var result = await response.json();
        if (!result.ok) {
          verifyButton.classList.remove('is-loading');
          showError(verify.invalidError || 'That code isn’t right — please try again.');
          return;
        }
        clearInterval(timer);
        if (data.channel === 'email') self.verifiedEmail = data.email;
        else self.verifiedPhone = '+' + data.phone;
        self.finish();
      } catch (error) {
        console.error('[Check My Home] Verify failed:', error);
        verifyButton.classList.remove('is-loading');
        showError(verify.invalidError || 'Something went wrong — please try again.');
      }
    });

    startTimer();
    inputs[0].focus();
  };

  /* ---------- Submission + thank you ---------- */

  Wizard.prototype.submissionFields = function () {
    var self = this;
    var fields = [];
    Object.keys(this.state).forEach(function (key) {
      var value = self.state[key];
      if (value === null || value === undefined || value === '') return;
      if (Array.isArray(value)) {
        if (value.length) fields.push({ name: key, value: value.join(';') }); /* HubSpot multi-checkbox format */
      } else if (typeof value === 'object') {
        var flat = value.fileUrl || value.address || value.postcode || null;
        if (flat) fields.push({ name: key, value: String(flat) });
      } else {
        fields.push({ name: key, value: String(value) });
      }
    });

    var fullName = this.state[this.nameField];
    if (fullName && fullName !== SKIPPED) {
      var parts = String(fullName).trim().split(/\s+/);
      fields.push({ name: 'firstname', value: parts.shift() });
      if (parts.length) fields.push({ name: 'lastname', value: parts.join(' ') });
    }
    if (this.verifiedEmail) fields.push({ name: 'email', value: this.verifiedEmail });
    if (this.verifiedPhone) fields.push({ name: 'phone', value: this.verifiedPhone });
    return fields;
  };

  Wizard.prototype.submitToHubSpot = async function () {
    var portalId = this.config.hubspotPortalId;
    var formId = this.config.hubspotFormId;
    if (!portalId || !formId) {
      console.warn('[Check My Home] HubSpot Portal ID / Form GUID not set — skipping form submission.');
      return;
    }
    var hutk = document.cookie.split('; ').filter(function (row) { return row.indexOf('hubspotutk=') === 0; })[0];
    try {
      var response = await fetch('https://api.hsforms.com/submissions/v3/integration/submit/' + portalId + '/' + formId, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          fields: this.submissionFields(),
          context: { pageUri: location.href, pageName: document.title, hutk: hutk ? hutk.split('=')[1] : undefined }
        })
      });
      if (!response.ok) {
        var body = await response.json().catch(function () { return null; });
        console.error('[Check My Home] HubSpot form submission failed:', response.status, body);
      }
    } catch (error) {
      console.error('[Check My Home] HubSpot form submission error:', error);
    }
  };

  Wizard.prototype.finish = function () {
    this.furthestIndex = this.steps.length - 1;
    this.saveProgress();
    this.submitToHubSpot();
    this.renderThankYou();
  };

  Wizard.prototype.renderThankYou = function () {
    var thanks = this.config.thankYou || {};
    if (this.elements.progress) this.elements.progress.hidden = true;
    this.elements.stage.innerHTML =
      '<div class="voltx-cmh__card voltx-cmh__card--thanks">' +
      '<span class="voltx-cmh__card-check" aria-hidden="true">' + ICONS.check + '</span>' +
      '<p class="voltx-cmh__card-heading h4" tabindex="-1">' + escapeHtml(this.personalize(thanks.heading || 'Thanks, [Name] — we’re on it.')) + '</p>' +
      (thanks.body ? '<div class="voltx-cmh__card-text body">' + thanks.body + '</div>' : '') +
      (thanks.buttonLabel && thanks.buttonUrl
        ? '<a href="' + escapeHtml(thanks.buttonUrl) + '" class="btn btn--primary voltx-cmh__card-button">' + escapeHtml(thanks.buttonLabel) +
          '<span class="voltx-cmh__nav-icon voltx-cmh__nav-icon--light">' + ICONS.arrow + '</span></a>'
        : '') +
      '</div>';
    var heading = this.elements.stage.querySelector('.voltx-cmh__card-heading');
    if (heading) heading.focus();
  };

  /* ---------- Boot ---------- */

  function init() {
    document.querySelectorAll('[data-voltx-check-my-home]').forEach(function (root) {
      new Wizard(root).init();
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
